import { movies, type MediaItem, type MediaType } from "@/mocks/movies";

export function getMediaById(id: string): MediaItem | undefined {
  return movies.find((item) => item.id === id);
}

export function getByType(type: MediaType): MediaItem[] {
  return movies.filter((item) => item.type === type);
}

export function getFeatured(): MediaItem[] {
  return movies.filter((item) => item.featured);
}

export function getLatest(limit?: number): MediaItem[] {
  const sorted = [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getPopular(limit?: number): MediaItem[] {
  const sorted = [...movies].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelated(current: MediaItem, limit = 4): MediaItem[] {
  const scored = movies
    .filter((item) => item.id !== current.id)
    .map((item) => {
      let score = 0;
      if (item.type === current.type) score += 1;
      item.genres.forEach((genre) => {
        if (current.genres.includes(genre)) score += 2;
      });
      if (current.tags.some((tag) => item.tags.includes(tag))) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export interface FilterOptions {
  genre?: string;
  yearRange?: { min: number; max: number } | null;
  sort?: "default" | "updated";
}

export function filterMedia(type: MediaType, options: FilterOptions): MediaItem[] {
  let list = getByType(type);
  if (options.genre && options.genre !== "全部") {
    list = list.filter((item) => item.genres.includes(options.genre as string));
  }
  if (options.yearRange) {
    list = list.filter(
      (item) => item.year >= options.yearRange!.min && item.year <= options.yearRange!.max
    );
  }
  if (options.sort === "updated") {
    list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
  return list;
}

export function searchMedia(keyword: string, filterType?: MediaType | "all"): MediaItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  const matched = movies.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.subtitleInfo,
      item.director,
      item.region,
      ...item.cast,
      ...item.genres,
      ...item.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
  const typed =
    filterType && filterType !== "all"
      ? matched.filter((item) => item.type === filterType)
      : matched;
  return [...typed].sort((a, b) => b.views - a.views);
}

export function formatViews(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return `${value}`;
}

export function formatDate(value: string): string {
  const [year, month, day] = value.split("-");
  return `${year}年${month}月${day}日`;
}

export const TYPE_LABEL: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
};